<script setup lang="ts">
import { type GameTileProps, type Game, GameAssetTypes, GameTileType, type GameClickEventEmit, type GameProviderSlug, type UseGameStartOptions } from '@game/types'
import { BroadcastMessageType, type BroadcastMessage, type ImageModifiers, type ImageOptions } from '@core/types'
import { useAssetFallback, useGameAssets, useIntervalFn, useGameConfig, useDevice, useGameType, useCoreConfig, useUserInfo, useThemeLogo, useThemeIllustration, computed, onMounted, ref, useImage, useFormat, useRoute, watch, usePlaygameState, useGame, useGameTile, useTimeoutFn, useSharedBroadcast } from '#imports'
import { useI18n } from 'vue-i18n'

const { translations, bingo, promotion } = useGameConfig()
const { company } = useCoreConfig()
const brandIcon = useThemeLogo('icon')
const brandTileBackground = useThemeIllustration('tileBackground')

const props = defineProps<GameTileProps>()

const emit = defineEmits<GameClickEventEmit & {
    loaded: []
}>()

const route = useRoute()
const { asMoney } = useFormat()
const { isMobile } = useDevice()
const { t } = useI18n()

const game = computed((): Game => props.game)
const { assetUrl, videoAssetUrl, loadVideo, isVideoTile: _isVideoTile, isVideoSmallTile: _isVideoSmallTile, hasAsset, firstAvailable } = useGameAssets()
const { isLoggedIn } = useUserInfo()
let isOneAtATimeRestricted: (gameType: string | undefined, provider: number | GameProviderSlug, slug?: string) => boolean = () => false
let isPlayingGameAlready: (slug: string) => boolean = () => false
if (import.meta.client) {
    const _ps = usePlaygameState()
    isOneAtATimeRestricted = _ps.isOneAtATimeRestricted
    isPlayingGameAlready = _ps.isPlayingGameAlready
}

const { isLiveCasino, isBingo } = useGameType()

const {
    jackpot, superJackpot, megaprize, isBingoSlot, jackpotTypeText, jackpotsLoaded,
    isClosed, isDisabled, isPlayable,
    gameHasSpins, gameHasTickets, gameHasPromotion, gameHasPrizeDraw,
} = useGameTile(game)
let start: (options: UseGameStartOptions) => Promise<void> = async () => {}
if (import.meta.client) {
    start = useGame().start
}

const videoFailed = ref(false)

const shouldLoadVideo = computed(() => {
    if (!loadVideo.value) return false
    if (videoFailed.value) return false
    if (props.isMini) return false
    if (!props.videoEnabled) return false

    return true
})

const shouldMountVideo = computed(() => shouldLoadVideo.value && (props.loadMedia ?? true))

const isVideoTile = computed(() => {
    if (!shouldLoadVideo.value) return false
    if (!hasAsset(game.value, GameAssetTypes.VIDEO)) return false
    if (props.enforceVideoType === GameTileType.VIDEO) return true

    return _isVideoTile(game.value, props.videoEnabled)
})

const isVideoSmallTile = computed(() => {
    if (!shouldLoadVideo.value) return false
    if (!hasAsset(game.value, GameAssetTypes.VIDEO_SMALL)) return false
    if (props.enforceVideoType === GameTileType.VIDEO_SMALL) return true

    return _isVideoSmallTile(game.value, props.videoEnabled)
})

const tileRef = ref<HTMLElement>()
const tileWidth = ref(0)
const tileHeight = ref(0)

onMounted(() => {
    const el = tileRef.value
    if (!el) return

    tileWidth.value = Math.round(el.offsetWidth)
    tileHeight.value = Math.round(el.offsetHeight)
})

const backgroundSizes = computed(() => {
    if (isVideoTile.value) {
        return '340px 500px:310px 768px:340px 1024px:465px 1366px:510px 1920px:760px 2560px:1020px'
    }

    // Inline regular tiles are fixed-pixel: 135×160 mobile, 160×190 desktop (≥1024px).
    if (props.isInline && !props.isMini) {
        return '135px 1024px:160px'
    }

    return '170px 500px:155px 768px:170px 1024px:230px 1366px:253px 1920px:380px 2560px:510px'
})

// Sending both w and h (with fit=crop) makes Cloudflare crop the landscape
// source to the tile's aspect ratio. Without this, `object-fit: cover` would
// upscale the wider-than-tall source to cover taller tile shapes.
const backgroundDimensions = computed(() => {
    if (isVideoTile.value) {
        return { width: 2, height: 1 }
    }
    if (props.isInline && !props.isMini) {
        return { width: 16, height: 19 }
    }
    if (props.isMini) {
        return { width: 135, height: 88 }
    }
    return { width: 1, height: 1 }
})

const videoCdnSrc = computed(() => {
    const type = isVideoSmallTile.value ? GameAssetTypes.VIDEO_SMALL : GameAssetTypes.VIDEO

    return videoAssetUrl(game.value, type, tileWidth.value, tileHeight.value)
})

const img = useImage() as (src: string, modifiers?: ImageModifiers, options?: ImageOptions) => string

const defaultPixels = 90

function imageSet(src: string, w: number, h: number) {
    const opts = { preset: 'local', provider: 'cloudflare' } as ImageOptions
    const img1x = img(src, { w, h, quality: 50 }, opts)
    const img2x = img(src, { w, h, quality: 50, dpr: 2 }, opts)

    return `image-set(url('${img1x}') 1x, url('${img2x}') 2x)`
}

const flagBgUrl = computed(() => {
    if (!bingo?.flag_url) return ' '

    return imageSet(bingo.flag_url, 24, 18)
})

const freeSpinsUrl = computed(() => {
    if (!promotion?.free_spin_url) return ''

    return imageSet(promotion.free_spin_url, defaultPixels, defaultPixels)
})

const ticketsUrl = computed(() => {
    if (!promotion?.tickets_url) return ''

    return imageSet(promotion.tickets_url, defaultPixels, defaultPixels)
})

const promotionUrl = computed(() => {
    if (!promotion?.game_promotion_url) return ''

    return imageSet(promotion.game_promotion_url, defaultPixels, defaultPixels)
})

const prizeDrawUrl = computed(() => {
    return imageSet('/media/prize-draw-label.png', defaultPixels, defaultPixels)
})

const exclusiveUrl = computed(() => {
    if (!promotion?.exclusive_url) return ''

    return imageSet(promotion.exclusive_url, defaultPixels, defaultPixels)
})

const tileLabel = computed((): (string | boolean) => {
    if (props.isMini) return false

    if (gameHasPrizeDraw(game.value.slug)) {
        return `<span class="label-image promotion-icon" style="background-image: ${prizeDrawUrl.value}"></span>`
    }

    if (gameHasPromotion(game.value.slug)) {
        return `<span class="label-image promotion-icon" style="background-image: ${promotionUrl.value}"></span>`
    }

    if (gameHasTickets(game.value.slug)) {
        return `<span class="label-image promotion-icon" style="background-image: ${ticketsUrl.value}"></span>`
    }

    if (gameHasSpins(game.value.slug)) {
        return `<span class="label-image promotion-icon" style="background-image: ${freeSpinsUrl.value}"></span>`
    }

    if (game.value.tile_decoration === 'exclusive') {
        return `<span class="label-image promotion-icon" style="background-image: ${exclusiveUrl.value}"></span>`
    }

    if (game.value.tile_decoration === 'hot') {
        return translations.gameTile.recommended
    }

    if (game.value.tile_decoration === 'new') {
        return translations.gameTile.new
    }

    if (bingo?.flag_url && game.value?.slug === 'bingo') {
        return `<span class="label-image flag" style="background-image: ${flagBgUrl.value}"></span>`
    }

    return false
})

const disabledReason = computed(() => {
    if (!route.meta.isPlaygame) return null
    if (isPlayingGameAlready(game.value.slug)) return t('game.gameTile.alreadyPlaying')
    if (isOneAtATimeRestricted(game.value.game_type, game.value.provider_slug as GameProviderSlug)) return t('game.gameTile.oneAtATime')

    return null
})

const isAllowed = computed(() => !disabledReason.value)

const gameTileClasses = computed(() => {
    const classList: string[] = []

    if (!isPlayable.value || !isAllowed.value) {
        classList.push('disabled')

        return classList
    }

    if (props.isMini) {
        classList.push('tile-mini')

        return classList
    }

    // Generic tile classes
    if (game.value.has_jackpot || isBingo(game.value.game_type)) {
        classList.push('jackpot')
    }

    if (isVideoTile.value) {
        classList.push('video')
    }

    if (isVideoSmallTile.value) {
        classList.push('video-small')
    }

    // Tile decorations
    if (gameHasPrizeDraw(game.value.slug)) {
        classList.push('prize-draw')

        return classList
    }

    if (gameHasPromotion(game.value.slug)) {
        classList.push('promotion')

        return classList
    }

    if (gameHasTickets(game.value.slug)) {
        classList.push('has-tickets')

        return classList
    }

    if (gameHasSpins(game.value.slug)) {
        classList.push('has-spins')

        return classList
    }

    if (game.value.tile_decoration === 'exclusive') {
        classList.push('exclusive')

        return classList
    }

    if (game.value.tile_decoration === 'hot') {
        classList.push('hot')

        return classList
    }

    if (game.value.tile_decoration === 'new') {
        classList.push('new')

        return classList
    }

    return classList
})

const tileClass = computed((): (string | null) => {
    const tileBaseName = 'game-tile'

    return gameTileClasses.value.map(x => `${tileBaseName}--${x}`).join(' ')
})

// ==============
// Refs
// ==============
const { src: logoSrc, isFallback: logoIsFallback, onError: onLogoError } = useAssetFallback(
    () => firstAvailable(
        game.value,
        ...(isLoggedIn.value ? [GameAssetTypes.LOGO_AFTER] : []),
        GameAssetTypes.LOGO,
    ),
    () => brandIcon.value!.src,
)
const { src: backgroundSrc, isFallback: backgroundIsFallback, onError: onBackgroundError } = useAssetFallback(
    () => firstAvailable(game.value, GameAssetTypes.TILE),
    () => brandTileBackground.value!.src,
)

function onBackdropError() {
    // If the fallback also fails, unstick the parent skeleton — it only
    // hides on `loaded`, and another error from the same <NuxtImg> would
    // otherwise leave it spinning forever.
    if (backgroundIsFallback.value) {
        emit('loaded')
        return
    }
    onBackgroundError()
}

const videoPoster = assetUrl(game.value, GameAssetTypes.VIDEO_POSTER)
const videoSmallPoster = assetUrl(game.value, GameAssetTypes.VIDEO_SMALL_POSTER)

const logoWidth = computed(() => {
    if (props.isMini) return 90
    if (props.isInline) return 115

    return 155
})

const logoHeight = computed(() => {
    if (props.isMini) return 59
    if (props.isInline) return 99

    return 109
})

const logoSizes = computed(() => {
    if (props.isMini) return '90px lg:115px'
    if (props.isInline) return '115px md:140px'

    return '155px sm:170px md:200px xl:253px'
})

const openingGame = ref(false)
let startOpeningTimeout: () => void = () => {}

if (import.meta.client) {
    const _timeout = useTimeoutFn(() => {
        openingGame.value = false
    }, 5000, { immediate: false })
    startOpeningTimeout = _timeout.start

    const { onMessage } = useSharedBroadcast()
    onMessage((message: BroadcastMessage) => {
        if (message?.type !== BroadcastMessageType.GAME_TILE_STOP_LOADING) return
        openingGame.value = false
    })
}

// Start game
async function startGame() {
    if (isDisabled.value || !isAllowed.value) return

    emit('game-click', game.value)
    openingGame.value = true
    const isPlaygame = Boolean(route.meta?.isPlaygame)

    await start({ gameSlug: game.value.slug, gameInstanceId: game.value.instance_id, providerSlug: game.value.provider_slug, isPlaygame, game: game.value })

    startOpeningTimeout()
}

const miniTileButtonText = computed(() => {
    if (openingGame.value) return t('game.gameTile.opening_game')

    return t('game.savedGames.continue')
})

// Hover state
const isHovered = ref(false)
const canHover = import.meta.client ? useMediaQuery('(hover: hover)') : ref(false)

function onMouseEnter() {
    isHovered.value = true
}

function onMouseLeave() {
    isHovered.value = false
}

// Mobile animation toggle between jackpot and play button
const mobileToggle = ref(false)

const hasJackpotDisplay = computed(() => {
    return !isClosed.value && (
        game.value.has_jackpot
        || isBingo(game.value.game_type)
        || isBingoSlot.value
    )
})

const showPlayButtonElement = computed(() => isVideoTile.value || (isVideoSmallTile.value && hasJackpotDisplay.value))

// Only toggle on touch devices (no hover capability)
const shouldToggle = computed(() => isVideoSmallTile.value && hasJackpotDisplay.value && !canHover.value)

// Computed visibility states based on hover and mobile toggle
const showPlayButton = computed(() => {
    if (isVideoTile.value) return true
    if (isHovered.value) return true
    if (shouldToggle.value) return mobileToggle.value
    return false
})

const showJackpot = computed(() => {
    if (isHovered.value) return false
    if (shouldToggle.value) return !mobileToggle.value
    return true
})

function onLoadedTile() {
    emit('loaded')
}

const video = ref<HTMLVideoElement>()

function onVideoLoadedData() {
    const el = video.value

    if (!el) return

    el.play()
        .then(() => onLoadedTile())
        .catch(() => { videoFailed.value = true })
}

if (import.meta.client) {
    const { pause, resume } = useIntervalFn(() => {
        mobileToggle.value = !mobileToggle.value
    }, 3_000, { immediate: false })

    watch(shouldToggle, (toggle) => {
        if (toggle) {
            resume()
            return
        }
        pause()
        mobileToggle.value = false
    }, { immediate: true })
}
</script>

<template>
    <div
        v-if="game?.slug"
        ref="tileRef"
        class="game-tile"
        :class="[tileClass, { 'game-tile--hovered': isHovered }]"
        data-test="game-tile"
        tabindex="0"
        @click="startGame"
        @keyup.enter="startGame"
        @mouseenter="onMouseEnter"
        @mouseleave="onMouseLeave"
    >
        <div
            :class="['background', {
                'game-opening': openingGame,
                'video-tile': isVideoTile || isVideoSmallTile
            }]"
        >
            <div v-if="isVideoTile || isVideoSmallTile" class="video-container">
                <NuxtImg
                    class="background__video-poster-backdrop"
                    provider="cloudflare"
                    preset="gameservice"
                    :src="isVideoSmallTile ? videoSmallPoster : videoPoster"
                    :sizes="backgroundSizes"
                    :width="backgroundDimensions.width"
                    :height="backgroundDimensions.height"
                    fit="crop"
                    densities="1x"
                    :alt="`${game.name} - ${translations.gameTile[game.game_type]} - ${company?.websiteName}`"
                    decoding="async"
                    :loading="props.eager ? 'eager' : 'lazy'"
                    @load="onLoadedTile"
                />

                <video
                    v-if="tileWidth && shouldMountVideo"
                    ref="video"
                    :width="tileWidth"
                    :height="tileHeight"
                    autoplay
                    loop
                    muted
                    playsinline
                    disableremoteplayback
                    decoding="async"
                    preload="none"
                    :title="`${game.name} - ${translations.gameTile[game.game_type]} - ${company?.websiteName}`"
                    @loadeddata="onVideoLoadedData"
                >
                    <source
                        :src="videoCdnSrc"
                        type="video/mp4"
                        @error="videoFailed = true"
                    >
                </video>

                <p v-if="!isMini" class="game-loader">
                    {{ translations.gameTile.opening_game }}
                </p>
            </div>

            <NuxtImg
                v-else
                class="background__backdrop"
                provider="cloudflare"
                :preset="backgroundIsFallback ? 'local' : 'gameservice'"
                :src="backgroundSrc"
                :sizes="backgroundSizes"
                :width="backgroundDimensions.width"
                :height="backgroundDimensions.height"
                :densities="isLiveCasino(game.game_type, game.game_subtype) ? '1x 2x' : '1x'"
                quality="70"
                fit="crop"
                :alt="`${game.name} baggrund`"
                :loading="props.eager ? 'eager' : 'lazy'"
                decoding="async"
                @error="onBackdropError"
                @load="onLoadedTile"
            />

            <div
                v-if="!isVideoTile"
                :class="['background__logo', {
                    'background__logo--pulled': game.has_jackpot || isClosed,
                    'background__logo--fallback': logoIsFallback
                }]"
            >
                <NuxtImg
                    provider="cloudflare"
                    :preset="logoIsFallback ? 'local' : 'gameservice'"
                    :src="logoSrc"
                    :width="logoWidth"
                    :height="logoHeight"
                    :sizes="logoSizes"
                    :alt="`${game.name} - ${translations.gameTile[(game.game_type)]} - ${company?.websiteName}`"
                    loading="lazy"
                    decoding="async"
                    @error="onLogoError"
                />

                <p v-if="!isMini" class="game-loader">
                    {{ translations.gameTile.opening_game }}
                </p>
            </div>
        </div>

        <div class="content">
            <div class="content__upper">
                <p
                    class="content__name"
                    :class="{ 'content__name--labelled': tileLabel, 'content__name--wrap': tileLabel && game.name.length > 15 }"
                >
                    <span>{{ game.name }}</span>
                </p>
                <!-- eslint-disable vue/no-v-html tileLabel is safe -->
                <div
                    v-if="tileLabel"
                    class="content__label"
                    v-html="tileLabel"
                />
                <!-- eslint-enable vue/no-v-html -->
            </div>

            <div class="content__lower">
                <p v-if="game.is_staff_only" class="content__staff-only">
                    Staff only
                </p>
                <p v-if="isLiveCasino(game.game_type, game.game_subtype) && !isClosed" class="content__live-label">
                    Live
                </p>
                <p v-if="disabledReason" class="content__opening">
                    {{ disabledReason }}
                </p>
                <p v-else-if="isClosed" class="content__opening">
                    {{ isClosed }}
                </p>
                <p
                    v-else-if="game.has_jackpot || isBingo(game.game_type) || isBingoSlot"
                    class="content__jackpot"
                    :class="{
                        'content__jackpot--animated': isVideoSmallTile,
                        'content__jackpot--hidden': isVideoSmallTile && !showJackpot,
                        'content__jackpot--loading': !jackpotsLoaded,
                    }"
                >
                    <template v-if="jackpotsLoaded">
                        <span class="content__jackpot-text">{{ jackpotTypeText }}</span>
                        <span v-if="isBingo(game.game_type) && jackpot" class="content__jackpot-amount">{{ asMoney(jackpot, { decimals: 0 }) }}</span>
                        <span v-else-if="megaprize" class="content__jackpot-amount">{{ asMoney(megaprize, { decimals: 0 }) }}</span>
                        <span v-else-if="superJackpot" class="content__jackpot-amount">{{ asMoney(superJackpot, { decimals: 0 }) }}</span>
                    </template>
                </p>
                <!-- Empty placeholder for video tiles without jackpot -->
                <p v-else-if="isVideoTile" class="content__jackpot content__jackpot--placeholder">
                    <span />
                </p>

                <!-- Play button for video tiles -->
                <Transition name="play-button">
                    <UIButton
                        v-if="showPlayButtonElement && (isVideoTile || showPlayButton)"
                        class="content__play-now-button"
                        size="xs"
                        as="cta"
                    >
                        {{ t('game.gameTile.play_now') }} <UIIcon name="play" size="8" />
                    </UIButton>
                </Transition>

                <UIButton
                    v-if="isMini"
                    class="content__continue-button"
                    color="secondary"
                    size="xs"
                >
                    {{ miniTileButtonText }} <UIIcon name="play" size="8" />
                </UIButton>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.game-tile {
    position: relative;
    overflow: hidden;

    transform: translateZ(0);
    border-radius: $game-tile-border-radius;
    box-shadow: $game-tile-box-shadow;
    cursor: pointer;
    user-select: none;
    transition: box-shadow 0.5s $easeOutQuad;

    &--video {
        height: 100%;
        grid-column: span 2;
    }

    p {
        margin: 0;
    }

    .background {
        display: grid;
        position: absolute;
        top: 0;
        left: 0;
        justify-content: center;
        align-items: center;
        width: 100%;
        height: 100%;
        overflow: hidden;
        border-radius: $game-tile-border-radius; // Needed for rounded corners in Safari

        &::before {
            position: absolute;
            top: 0;
            left: 0;
            z-index: 1;
            width: 100%;
            height: 60px;
            border-top-left-radius: $game-tile-border-radius;
            border-top-right-radius: $game-tile-border-radius;
            opacity: 0.6;
            background: transparent linear-gradient(0deg, #00000000 0%, #000000e6 100%);
            content: '';
        }

        .video-container {
            position: relative;
            top: 0;
            left: 0;
            height: 100%;
            width: 100%;

            .game-loader {
                left: 0;
                right: 0;
                text-align: center;
            }
        }

        video,
        &__backdrop,
        &__video-poster-backdrop {
            grid-area: 1 / 1;
            min-width: 100%;
            min-height: 100%;
            object-fit: cover;
            justify-self: center;
        }

        video,
        &__backdrop,
        &__video-poster-backdrop {
            position: absolute;
            top: 0;
            right: 0;
            bottom: 0;
            left: 0;
            grid-area: initial;
            width: 100%;
            height: 100%;
        }

        &__backdrop {
            filter: brightness(100%);
            transform: translateZ(0);
            transition: 0.4s $easeOutQuad;
            border-radius: $game-tile-border-radius; // Needed for rounded corners in Safari
        }

        &__logo {
            display: flex;
            grid-area: 1 / 1;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            align-self: center;
            width: 100%;
            max-width: calc(100% - 10px);
            height: 100%;
            justify-self: center;
            will-change: transform;
            transition: transform 0.4s $easeOutQuad;

            @media (min-width: $screen-sm) {
                max-width: calc(100% - 20px);
            }

            @include tablet {
                max-width: calc(100% - 30px);
            }

            img {
                width: auto;
                height: auto;
                max-height: 60%;
                max-width: 100%;
                transform: translateZ(0); // Hack to fix blurry images with decimal dimentions
                object-fit: contain;

                // Prevent dragging of image
                -webkit-user-drag: none;
                -khtml-user-drag: none;
                -moz-user-drag: none;
                -o-user-drag: none;
            }

            &--pulled {
                margin-top: -5px;
            }

            // Fallback logo is the active brand icon — a decorative
            // placeholder, not the game's own art. Shrink and soften it so
            // it reads as "image missing" rather than competing visually
            // with real game logos around it.
            &--fallback img {
                max-height: 35%;
                max-width: 55%;
                opacity: 0.5;
            }

            .game-list--inline & {
                @include tablet {
                    max-width: calc(100% - 20px);
                }
            }
        }

        .game-loader {
            position: absolute;
            bottom: 0;
            font-size: $font-size-sm;
            font-weight: $text-semi-bold;
            color: $white;
            transform: translateY(20px);
            opacity: 0;
            transition: all 0.3s ease-in-out;
        }

        &.game-opening {
            video,
            .background__backdrop {
                filter: brightness(50%);
            }

            &.video-tile {
                .game-loader {
                    display: none;
                }

                .video-container {
                    .game-loader {
                        display: block;
                        transform: translateY(-40px);
                        opacity: 1;
                    }
                }

                .background__logo {
                    transform: translateY(0px) translateZ(0) !important; // important is used to override the :hover and :active styling further down
                }
            }

            .background__logo {
                .game-loader {
                    opacity: 1;
                    transform: translateY(-10px);

                    @include tablet {
                        transform: translateY(-25px);
                    }
                }
            }
        }
    }

    .content {
        display: flex;
        position: relative;
        z-index: 1;
        flex-direction: column;
        width: 100%;
        height: 100%;

        &::after {
            content: '';
            position: relative;
            padding-bottom: 100%;
        }

        &__upper {
            display: flex;
            position: absolute;
            flex-wrap: nowrap;
            align-items: flex-start;
            width: 100%;
            height: 55px;
            border-top-left-radius: $game-tile-border-radius; // Needed for rounded corners in Safari
            border-top-right-radius: $game-tile-border-radius; // Needed for rounded corners in Safari
        }

        &__label {
            width: 100px;
            margin: -9px -39px auto -30px;
            padding: 20px 0 0;
            transform: rotate(45deg);
            color: $game-tile-label-color;
            font-size: $game-tile-label-size-xs;
            font-weight: $text-heavy;
            text-align: center;

            @media (min-width: $screen-sm) {
                margin-left: -20px;
                font-size: $game-tile-label-size-sm;
            }

            :deep(.label-image) {
                background-size: cover;
                background-repeat: no-repeat;
                background-position: center center;
            }

            :deep(.flag) {
                position: relative;
                display: inline-block;
                width: 24px;
                height: 18px;
                transform: rotate(-45deg) translate(-8px, 1px);
                border-radius: 4px;
                overflow: hidden;
            }

            :deep(.promotion-icon) {
                position: absolute;
                width: 90px;
                height: 90px;
                transform: rotate(-45deg) translate(-20px, -45px);
            }
        }

        &__name {
            visibility: hidden;
            padding: 8px 10px;
            overflow: hidden;
            color: $game-tile-name-color;
            font-family: $body-font-family;
            font-size: $game-tile-name-size-xxs;
            font-weight: $text-extra-bold;
            text-overflow: ellipsis;
            text-align: left;

            @media (min-width: 500px) {
                visibility: visible;
                font-size: $game-tile-name-size-sm;
            }

            span {
                white-space: nowrap;
            }

            // On smaller devices wrap the title to ensure it's readable
            &--wrap {
                span {
                    white-space: normal;

                    @media (min-width: 500px) {
                        white-space: nowrap;
                    }
                }
            }

            &--labelled {
                width: calc(
                    100% - 25px
                ); // 30px is used to make sure that the name isn't too long, so the label can't fit.

                @media (min-width: $screen-sm) {
                    width: calc(
                        100% - 40px
                    ); // 60px is used to make sure that the name isn't too long, so the label can't fit.
                }
            }
        }

        &__lower {
            display: flex;
            position: absolute;
            bottom: 0;
            width: 100%;
            margin: auto 0 0;
            padding: 6px;
            gap: 6px;

            .game-tile--video & {
                display: grid;
                grid-template-columns: 8fr 3fr;
            }
        }

        &__staff-only {
            position: absolute;
            top: 50%;
            left: 0;
            padding: 5px 8px;
            transform: rotate(90deg) translate(-100%, 82%);
            border-radius: 5px 5px 0 0;
            color: #000;
            background: $gold;
            font-family: $body-font-family;
            font-size: $font-size-sm;
            font-weight: 600;
        }

        &__live-label {
            position: absolute;
            bottom: 10px;
            margin: 0 0 0 5px;
            padding: 2px 8px;
            border-radius: $border-radius;
            color: $game-tile-label-live-color;
            background-color: $game-tile-label-live-bg;
            font-size: $font-size-xs;
            font-weight: $text-extra-bold;
            text-transform: uppercase;
        }

        &__opening,
        &__jackpot {
            display: flex;
            flex-direction: row;
            align-items: center;
            place-items: baseline;
            padding: 5px 6px;
            line-height: 1;
            border-radius: $game-tile-border-radius;
            color: white;
            background-color: rgb(0 0 0 / 50%);
            font-family: $body-font-family;
            font-size: $game-tile-jackpot-font-size-xxs;
            font-weight: $text-extra-bold;
            white-space: nowrap;
            text-transform: uppercase;
            text-overflow: ellipsis;
            gap: 6px;

            @include tablet {
                padding: 6px 8px 5px;
                font-size: $game-tile-jackpot-font-size-md;
            }

            .game-list--inline & {
                place-items: center;
                padding: 4px 5px;
                font-size: 11px;
                line-height: 1;
            }
        }

        &__opening {
            flex: 0 0 100%;
            justify-content: center;
        }

        &__jackpot {
            width: 100%;

            .game-list--inline & {
                @media (min-width: $screen-lg) {
                    font-size: $game-tile-inline-jackpot-font-size;
                }
            }

            &--placeholder {
                background-color: transparent;
                min-height: $game-tile-jackpot-font-size-xxs;
            }
        }

        &__jackpot-text {
            max-width: 13ch;
            text-overflow: ellipsis;
            overflow: hidden;
        }

        &__jackpot-amount {
            margin-left: auto;
            color: $gold;
            font-size: inherit;
            font-size: 1.12em;
            transform: translateY(1px); // Offset the bigger font-size

            .game-list--inline & {
                font-size: 11px;

                @media (min-width: $screen-lg) {
                    font-size: 13px;
                }
            }
        }

        &__play-now-button,
        &__continue-button {
            min-height: unset;
            padding: 0;
            font-size: $game-tile-jackpot-font-size-xxs;
            line-height: 1;
            position: absolute;
            left: 6px;
            right: 6px;
            bottom: 6px;
            width: auto;
            border-radius: $game-tile-border-radius;

            @include tablet {
                font-size: $game-tile-jackpot-font-size-md;
            }

            :deep(.btn__content) {
                align-items: center;
                transition: all 0.4s $easeOutQuart;

                .icon {
                    align-self: center;
                    width: 8px;
                    height: 8px;
                    margin-right: unset;
                    transition: all 0.4s $easeOutQuart;

                    @include tablet {
                        width: 10px;
                        height: 10px;
                    }
                }
            }

            :deep(.btn__container) {
                min-height: unset;
                padding: 6px 10px 5px;

                @include tablet {
                    padding: 6px 12px;
                }
            }

            .game-list--inline & {
                :deep(.btn__content) {
                    .icon {
                        width: 6px;
                        height: 6px;
                    }
                }
            }
        }

        &__continue-button {
            left: 0;
            right: 0;
            bottom: 0;
            border-radius: 0;
            text-align: left;

            @include tablet {
                font-size: 12px;
            }

            :deep(.btn__container) {
                justify-content: stretch;
            }

            :deep(.btn__content) {
                justify-content: space-between;
            }
        }

        &__play-now-button {
            .game-list--inline & {
                font-size: 11px;

                :deep(.btn__container) {
                    padding: 4px 5px;
                }
            }
        }

        // Play button transition
        .play-button-enter-active,
        .play-button-leave-active {
            transition:
                opacity 0.4s ease,
                transform 0.4s ease;
        }

        .play-button-enter-from,
        .play-button-leave-to {
            opacity: 0;
            transform: translateY(10px);
        }

        // Animated jackpot (controlled by JS hover/toggle state)
        &__jackpot--animated {
            transition:
                opacity 0.4s ease,
                transform 0.4s ease;

            &.content__jackpot--hidden {
                opacity: 0;
                transform: translateY(-10px);
            }
        }

        &__jackpot--loading {
            position: relative;
            overflow: hidden;

            &::before {
                content: '\00a0';
                visibility: hidden;
            }

            &::after {
                content: '';
                display: block;
                position: absolute;
                top: 0;
                right: 0;
                bottom: 0;
                left: 0;
                transform: translate3d(-100%, 0, 0);
                background-image: linear-gradient(90deg, transparent 0, $skeleton-loader-color 50%, transparent 100%);
                animation: jackpot-skeleton-load 1.5s ease-in-out infinite;
            }
        }
    }

    &--hovered {
        box-shadow: $game-tile-box-shadow-hover;

        .background {
            &__logo {
                transform: translateY(-5px) translateZ(0);
            }
        }
    }

    &:focus {
        box-shadow: $game-tile-box-shadow-hover;

        .background {
            &__logo {
                transform: translateY(-5px) translateZ(0);
            }
        }
    }

    &--video,
    &--video-small {
        .background {
            display: block;
        }
    }

    &--video {
        .content {
            &::after {
                padding-bottom: calc(50% - 15px);
            }

            &__play-now-button {
                position: static;
            }

            // Disable transition for video tiles (always visible)
            .play-button-enter-active,
            .play-button-leave-active {
                transition: none;
            }
        }
    }

    &--video-small {
        .content {
            &__name {
                visibility: hidden;
            }
        }

        .background {
            &__logo {
                position: absolute;
                top: 5px;
                left: 0px;
                height: 50%;
                z-index: 1;

                img {
                    position: absolute;
                    left: 10px;
                    top: 10px;
                }
            }
        }

        &.game-tile--hovered,
        &:focus {
            .background {
                &__logo,
                &__backdrop {
                    transform: none;
                }
            }
        }
    }

    &--hot {
        .content {
            &__label {
                color: $game-tile-label-hot-color;
                background: $game-tile-label-hot-bg;
            }
        }
    }

    &--new {
        .content {
            &__label {
                color: $game-tile-label-new-color;
                background: $game-tile-label-new-bg;
            }
        }
    }

    &--tile-mini {
        .background {
            &::before {
                display: none;
            }
        }

        .background__backdrop {
            width: 100%;
            height: auto;
        }

        .background__logo {
            height: calc(100% - 25px);
            align-self: flex-start;
            margin-top: 2px;
            aspect-ratio: 135/88;

            @include tablet {
                margin-top: 5px;
                height: calc(100% - 35px);
            }

            img {
                max-height: 100%;
            }
        }

        .content {
            &::after {
                padding-bottom: 65.45%; // Maintain aspect ratio 110:72
            }

            &__upper {
                display: none;
            }

            &__lower {
                padding: 0;

                & > *:not(.content__continue-button) {
                    display: none;
                }
            }
        }

        &:hover {
            .content__continue-button {
                :deep(.btn__content) {
                    padding: 1px 0;

                    .icon {
                        transform: translate3d(-2px, 0, 0) scale(1.5);
                    }
                }
            }
        }
    }

    &--featured {
        .content {
            &::after {
                padding-bottom: 50%;
            }
        }
    }

    &--has-tickets {
        .content {
            border: 3px solid $game-tile-label-tickets-bg;
            box-shadow: 0 0 10px 3px $game-tile-label-tickets-bg inset;
            animation: tickets-pulse 4s ease-in-out infinite;
            border-radius: inherit;

            &__label {
                padding: 18px 0 18px;
                color: $game-tile-label-tickets-color;
                background: $game-tile-label-tickets-bg;
            }
        }
    }

    &--has-spins {
        .content {
            border: 3px solid $game-tile-label-spins-bg;
            box-shadow: 0 0 10px 3px $game-tile-label-spins-bg inset;
            animation: spins-pulse 4s ease-in-out infinite;
            border-radius: inherit;

            &__label {
                padding: 18px 0 18px;
                color: $game-tile-label-spins-color;
                background: $game-tile-label-spins-bg;
            }
        }
    }

    &--promotion {
        .content {
            border: 3px solid $game-tile-label-promotion-bg;
            box-shadow: 0 0 10px 3px $game-tile-label-promotion-bg inset;
            border-radius: inherit;

            &__label {
                padding: 18px 0 18px;
                color: $game-tile-label-promotion-color;
                background: $game-tile-label-promotion-bg;
            }
        }
    }

    &--prize-draw {
        .content {
            border: 3px solid $game-tile-label-prize-draw-bg;
            box-shadow: 0 0 10px 3px $game-tile-label-prize-draw-bg inset;
            border-radius: inherit;

            &__label {
                padding: 18px 0 18px;
                background: $game-tile-label-prize-draw-bg;
            }
        }
    }

    &--exclusive {
        .content {
            border: 3px solid $game-tile-label-exclusive-bg;
            box-shadow: 0 0 10px 3px $game-tile-label-exclusive-bg inset;
            border-radius: inherit;

            &__label {
                padding: 18px 0 18px;
                color: $game-tile-label-exclusive-color;
                background: $game-tile-label-exclusive-bg;
            }
        }
    }

    @keyframes spins-pulse {
        0%,
        100% {
            box-shadow: 0 0 14px 2px $game-tile-label-spins-bg inset;
        }

        50% {
            box-shadow: 0 0 4px 1px $game-tile-label-spins-bg inset;
        }
    }

    @keyframes tickets-pulse {
        0%,
        100% {
            box-shadow: 0 0 14px 2px $game-tile-label-tickets-bg inset;
        }

        50% {
            box-shadow: 0 0 4px 1px $game-tile-label-tickets-bg inset;
        }
    }

    @keyframes jackpot-skeleton-load {
        to {
            transform: translate3d(100%, 0, 0);
        }
    }

    &--disabled {
        cursor: not-allowed;

        .background {
            filter: grayscale(90%);
        }

        .content {
            &__label {
                color: $white;
                background-color: $gray-light;
            }

            &__name {
                color: $gray-lighter;
            }
        }

        &.game-tile--hovered {
            box-shadow: $element-box-shadow; // Don't react

            .background {
                &__logo,
                &__backdrop {
                    transform: none;
                }
            }
        }
    }
}
</style>
